// Premium CCG route config for: /slough-to-heathrow
// Use this file as the first CCG route data template. Keep reusable layout in
// CcgRoutePremiumPage; keep route-specific SEO/content here.

import React from "react";
import { Link } from "react-router-dom";
import CcgRoutePremiumPage from "../components/CcgRoutePremiumPage";
import PostcodeQuotenew from "./PostcodeQuotenew";
import "./CcgRoutePremium.css";

const cardGrid = (className, cards) => (
  <div className={className}>
    {cards.map((card) => (
      <article className={card.className || ""} key={card.title}>
        <h3>{card.title}</h3>
        {card.body ? <p>{card.body}</p> : null}
        {card.items ? (
          <ul>
            {card.items.map((item) => <li key={item}>{item}</li>)}
          </ul>
        ) : null}
        {card.link ? (
          <Link to={card.link.to} className={card.link.className || "route-link-inline"}>
            {card.link.label}
          </Link>
        ) : null}
        {card.links ? (
          <div className="route-link-btn-row">
            {card.links.map((link) => (
              <Link to={link.to} className={link.className || "route-link-btn"} key={link.to}>
                {link.label}
              </Link>
            ))}
          </div>
        ) : null}
      </article>
    ))}
  </div>
);

const sloughToHeathrowRoute = {
  id: "slough-to-heathrow",
  path: "/slough-to-heathrow",
  liveStripVariant: "slough",
  seo: {
    title: "Slough to Heathrow Taxi – Fixed-Price Airport Transfers | CheapCabGrab",
    description:
      "Book a fixed-price taxi from Slough (SL1–SL3) to Heathrow. Instant online quote, licensed drivers, flight tracking, 24/7 support and no surge pricing with CheapCabGrab.",
    keywords:
      "Slough to Heathrow taxi, taxi to Heathrow Airport from Slough, SL taxi to Heathrow, Slough airport transfer, CheapCabGrab",
    canonical: "https://www.cheapcabgrab.com/slough-to-heathrow",
    ogTitle: "Slough to Heathrow Taxi – Fixed Airport Transfers SL1–SL3",
    ogDescription:
      "Reliable Slough to Heathrow taxis with fixed fares, licensed drivers and live flight tracking. Book online in seconds with CheapCabGrab.",
    serviceName: "CheapCabGrab – Slough to Heathrow Taxi",
    areaServed: ["Heathrow Airport", "Slough SL1", "Slough SL2", "Slough SL3"],
    offerCatalogName: "Slough to Heathrow Taxi Routes",
    offerName: "Slough to Heathrow Airport fixed-price taxi",
    offerDescription:
      "Fixed-price taxi transfers from Slough (SL1–SL3) to Heathrow Airport terminals with licensed drivers and flight tracking.",
    reservationName: "Taxi booking from Slough to Heathrow",
  },
  hero: {
    eyebrow: "Slough to Heathrow taxi route",
    h1: "Slough to Heathrow Taxi – Fixed-Price Airport Transfers",
    subtitle: "SL1–SL3 specialists for Heathrow terminals 2, 3, 4 and 5.",
    body:
      "Book reliable taxis from Slough to Heathrow with licensed drivers, flight tracking and secure online booking. Fixed upfront fares — no surge pricing.",
  },
  quote: {
    label: "Get instant quotes",
    note: "Free • Fast • No obligation",
    presetPickup: "Slough, SL1",
    presetDropoff: "Heathrow Airport",
  },
  flags: [
    "✅ Fixed fares agreed upfront — no meters",
    "✅ Live flight tracking for Heathrow arrivals",
    "✅ Licensed drivers, meet & greet, luggage-ready",
    "✔ Saloon, MPV, 8–16 seat minibus & coach options",
  ],
  reviewPageKey: "/slough-to-heathrow",
  reviewAriaLabel: "Slough to Heathrow customer reviews",
  lhrStatusTitle: "Live Heathrow checks for Slough to Heathrow taxis",
  floatingCta: "🚖 Book Slough → Heathrow Taxi",
  mobileCta: "🚖 Book Slough → Heathrow Taxi",
  supportNudge: "Need Slough to Heathrow help?",
  sections: [
    {
      id: "route-overview",
      title: "Slough ↔ Heathrow at a glance",
      body:
        "A short, busy airport corridor served 24/7 by CheapCabGrab. Ideal for residents, hotels, business parks and travellers connecting via Heathrow.",
      render: () => cardGrid("route-overview-grid", [
        {
          title: "Journey basics",
          items: [
            "📏 Distance: Approx. 8–12 miles (depending on pickup point)",
            "⏱ Typical time: 15–30 minutes",
            "💷 Typical saloon fare: from £35–£55",
            "🏙 Areas covered: SL1, SL2, SL3, Slough Trading Estate & more",
          ],
        },
        {
          title: "Great for",
          items: [
            "✈️ Residents flying from Heathrow",
            "🏨 Hotel guests in Slough, Langley & Burnham",
            "💼 Staff working on Slough Trading Estate",
            "👨‍👩‍👧 Families with luggage & child seats",
          ],
        },
        {
          title: "Why CheapCabGrab?",
          items: [
            "🚕 Licensed local drivers with airport experience",
            "🔍 Transparent fares, no surge pricing",
            "📱 Instant online booking & confirmation",
            "🧾 Receipts for business & expense claims",
          ],
        },
      ]),
    },
    {
      id: "route-tools",
      title: "Useful tools for your Slough ⇆ Heathrow journey",
      body: "Plan every part of your airport trip with CheapCabGrab tools.",
      render: () => cardGrid("route-links-grid", [
        {
          title: "✈ Heathrow Terminal Finder",
          body: "Not sure which terminal your airline uses? Use our live terminal finder to check gates and departure points.",
          link: { to: "/terminalFinder", label: "Open Terminal Finder", className: "route-link-btn" },
        },
        {
          title: "📞 Airline contact search",
          body: "Quickly find customer service numbers, websites and support links for your airline.",
          link: { to: "/airlineContacts", label: "Find airline contacts", className: "route-link-btn" },
        },
        {
          title: "🚐 Minibus & coach options",
          body: "Travelling as a group from Slough to Heathrow? Upgrade to a minibus or coach for extra space and comfort.",
          links: [
            { to: "/minibus", label: "Heathrow minibus" },
            { to: "/coach", label: "Heathrow coach", className: "route-link-btn route-link-btn-secondary" },
          ],
        },
      ]),
    },
    {
      id: "route-areas",
      title: "Slough → Heathrow – popular pickup & drop-off points",
      body: "We regularly serve homes, hotels and business locations across Slough and the Heathrow corridor.",
      render: () => cardGrid("route-areas-grid", [
        {
          title: "In and around Slough (SL1–SL3)",
          items: [
            "Slough town centre & bus station",
            "Slough train station",
            "Slough Trading Estate & Bath Road offices",
            "Wexham Park Hospital",
            "Langley, Colnbrook & Poyle",
          ],
          link: { to: "/slough-taxi", label: "Slough taxis overview →" },
        },
        {
          title: "Heathrow & nearby hotels",
          items: [
            "All Heathrow terminals (T2, T3, T4, T5)",
            "Airport hotels on Bath Road",
            "Hotels near M4 junctions & business parks",
          ],
          link: { to: "/heathrow-hotels", label: "See Heathrow hotel guide →" },
        },
      ]),
    },
    {
      id: "route-faq",
      title: "Slough to Heathrow taxi – FAQs",
      body: "A few common questions passengers ask when travelling between Heathrow Airport and Slough.",
      render: () => (
        <div className="route-faq-grid">
          {[
            ["Do you track my incoming flight?", "Yes. For Heathrow pickups we monitor your flight and adjust the dispatch time where possible. If your flight is severely delayed or diverted, just contact support and we will re-plan your pickup."],
            ["Can you collect from Slough train station or Trading Estate?", "Absolutely. Slough station, the bus station, Slough Trading Estate and Bath Road offices are all regular pickup and drop-off points for CheapCabGrab."],
            ["Is meet & greet at Heathrow included?", "Standard fares include curbside pickup. Meet & greet inside the terminal is available on request for an additional charge – ideal for visitors or vulnerable passengers."],
            ["Can I book a minibus from Slough to Heathrow?", "Yes. For larger groups, luggage or work teams, we offer 7–14 seater minibuses and 25+ seater coaches through our PSV partners. Use the quote form or visit our minibus and coach pages for more details."],
          ].map(([title, body]) => (
            <article className="route-faq-card" key={title}>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
          <div className="route-faq-footer">
            <p>Need more details? Visit our full <Link to="/faq">FAQ page</Link> or contact support.</p>
          </div>
        </div>
      ),
    },
    {
      id: "route-support",
      title: "Need help with a Slough ⇆ Heathrow taxi booking?",
      body: "If you have special requirements, multi-stop journeys or corporate bookings, our team is happy to help.",
      render: () => (
        <div className="route-support-box">
          <p>✉ Email: <a href="mailto:support@cheapcabgrab.com">support@cheapcabgrab.com</a></p>
          <p>Or send us a message via our <Link to="/contact">contact form</Link> and we’ll get back to you as soon as possible.</p>
          <p>
            For other popular routes, see our pages for{" "}
            <Link to="/slough-taxi">Slough taxis</Link>,{" "}
            <Link to="/heathrow-taxi">Heathrow taxis</Link> and our dedicated{" "}
            <Link to="/fleet">fleet</Link> and{" "}
            <Link to="/our-drivers">driver standards</Link> pages.
          </p>
        </div>
      ),
    },
  ],
};

const SloughToHeathrow = () => (
  <CcgRoutePremiumPage route={sloughToHeathrowRoute} QuoteComponent={PostcodeQuotenew} />
);

export default SloughToHeathrow;
